// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount, pluralize } = require('shared/string-utils')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const {
  CapsuleButton,
  CircleButton,
  UserFollowCapsuleButton
} = require('client-v2/common/components/buttons')
const ResponsiveImage = require('client-v2/common/components/responsive-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
const WithTooltip = require('client-v2/common/containers/tooltips/component-smart')
const {
  userProfileFollowersHref,
  userProfileFollowingHref
} = require('client-v2/common/models/user/actions')
const { normalizeExternalHref } = require('client-v2/common/state/router/utils')
// Entry container imports
const ProfileUserRecord = require('../profile-user/record')
const ProfileUserStatsRecord = require('../profile-user-stats/record')

const bem = BemEntity('UserProfileContentHeader')

const Followers = pluralize('Follower')

class UserProfileContentHeader extends React.PureComponent {
  constructor(props) {
    super(props)

    // Ensure function references do not change between render calls
    registerComponentEventHandlers(this)
  }

  // Button render methods

  renderButtons() {
    return (
      tag('div', {
        className: bem.element('buttons')
      },
        this.renderMoreButton(),
        this.renderShareButton(),
        this.renderFollowButton(),
        this.renderSettingsButton()
      )
    )
  }

  renderFollowButton() {
    if (this.props.isSelfProfile) {
      return null
    }
    const { profileUser } = this.props
    return (
      tag(WithTooltip, {
        tooltipKey: 'user-profile-follow',
        placement: ['left', 'bottom-end'],
        updateAfterResizeDelay: 300
      },
        tag(UserFollowCapsuleButton, {
          className: classNames(
            bem.element('button'),
            bem.element('follow-button')
          ),
          checked: profileUser.isFollowing,
          onClick: this.onFollowClick
        })
      )
    )
  }

  renderMoreButton() {
    if (!this.props.isLoggedIn || this.props.isSelfProfile) {
      return null
    }
    return (
      tag(CircleButton, {
        className: classNames(
          bem.element('button'),
          bem.element('more-button')
        ),
        title: 'Show more actions for this user',
        onClick: this.onMoreClick
      })
    )
  }

  renderSettingsButton() {
    if (!this.props.isSelfProfile) {
      return null
    }
    return (
      tag(CapsuleButton, {
        className: classNames(
          bem.element('button'),
          bem.element('settings-button')
        ),
        href: '/settings',
        title: 'Go to your settings page',
        onClick: this.onSettingsClick
      },
        'Settings'
      )
    )
  }

  renderShareButton() {
    return (
      tag(CircleButton, {
        className: classNames(
          bem.element('button'),
          bem.element('share-button')
        ),
        title: 'Share this user profile',
        onClick: this.onShareClick
      })
    )
  }

  // Render methods

  renderBanner() {
    const { profileUser } = this.props
    return tag(ResponsiveImage, {
      className: bem.element('banner'),
      imageUrls: profileUser['header_image_urls']
    })
  }

  renderBio() {
    const { profileUser } = this.props
    let { bio } = profileUser
    if (!bio && profileUser.mixesBio) {
      bio = `Posts in ${profileUser.mixesBio}`
    }
    if (!bio) {
      return tag('div', {
        className: bem.element('bio')
      })
    }
    return (
      tag('div', {
        className: bem.element('bio'),
        title: bio
      },
        bio
      )
    )
  }

  renderCustomUrl() {
    const customUrl = this.props.profileUser['custom_url']
    if (!customUrl) {
      return tag('div', {
        className: bem.element('custom-url')
      })
    }
    const href = normalizeExternalHref(customUrl)
    return (
      tag('a', {
        className: bem.element('custom-url'),
        href,
        rel: 'nofollow noopener',
        target: '_blank',
        title: href,
        onClick: this.onCustomUrlClick
      },
        href
      )
    )
  }

  renderDetails() {
    return (
      tag('div', {
        className: bem.element('details')
      },
        this.renderPhoto(),
        this.renderUserName(),
        this.renderFullName(),
        this.renderBio(),
        this.renderCustomUrl()
      )
    )
  }

  renderFullName() {
    const { profileUser } = this.props
    const fullName = profileUser['display_name']
    return (
      tag('div', {
        className: bem.element('full-name'),
        title: fullName
      },
        fullName
      )
    )
  }


  renderPhoto() {
    return tag(UserAvatar, {
      className: bem.element('photo'),
      user: this.props.profileUser,
      visible: true
    })
  }

  renderSocial() {
    const { profileUser, profileUserStats } = this.props
    return (
      tag('div', {
        className: bem.element('social')
      },
        this.renderSocialHref({
          count: profileUserStats.followers,
          href: userProfileFollowersHref(profileUser),
          label: Followers(profileUserStats.followers),
          onClick: this.onFollowersClick
        }),
        this.renderSocialHref({
          count: profileUserStats.friends,
          href: userProfileFollowingHref(profileUser),
          label: 'Following',
          onClick: this.onFollowingClick
        })
      )
    )
  }

  renderSocialHref({ count, href, label, onClick }) {
    const disabled = !this.props.isSelfProfile && !count
    return (
      tag('a', {
        className: bem.element('social-href'),
        href: disabled ? null : href,
        title: `${count} ${label}`,
        onClick: disabled ? null : onClick
      },
        tag('span', {
          className: bem.element('social-count')
        },
          formatCount(count)
        ),
        tag('label', {
          className: bem.element('social-label')
        },
          label
        )
      )
    )
  }

  renderUserName() {
    const { profileUser } = this.props
    const userName = `@${profileUser.username}`
    return (
      tag('div', {
        className: bem.element('user-name'),
        title: userName
      },
        userName
      )
    )
  }

  render() {
    return (
      tag('div', {
        className: bem.block()
      },
        this.renderBanner(),
        tag('div', {
          className: bem.element('container-wrapper')
        },
          tag('div', {
            className: bem.element('container-leading')
          },
            this.renderDetails()
          ),
          tag('div', {
            className: bem.element('container-trailing')
          },
            this.renderSocial(),
            this.renderButtons()
          )
        )
      )
    )
  }
}

UserProfileContentHeader.propTypes = {
  // Flags
  isLoggedIn: PropTypes.bool,
  isSelfProfile: PropTypes.bool,
  useNativeDefaultBanner: PropTypes.bool,
  // Values
  profileUser: PropTypes.shape(ProfileUserRecord.propTypes).isRequired,
  profileUserStats: PropTypes.shape(ProfileUserStatsRecord.propTypes).isRequired
}

// Callbacks
UserProfileContentHeader.eventHandlerKeys = [
  'onCustomUrlClick',
  'onFollowClick',
  'onFollowersClick',
  'onFollowingClick',
  'onMoreClick',
  'onSettingsClick',
  'onShareClick'
]
UserProfileContentHeader.eventHandlerKeys.forEach(key => {
  UserProfileContentHeader.propTypes[key] = PropTypes.func
})

module.exports = UserProfileContentHeader
